// Educational content layer. Drives the value sections on Service detail,
// Industry detail, Need detail, Home, and Services marketplace.
//
// Editable, plain-English, commercially grounded.
// No fake thought leadership. No invented stats. No published-content claims.

// ---------------------------------------------------------------------------
// CATEGORY-LEVEL EDUCATION
// One entry per service category slug. Each entry powers the value sections
// shown on every service detail page in that category.
// ---------------------------------------------------------------------------
export const CATEGORY_EDUCATION = {
  "strategy-and-audits": {
    whatThisIs:
      "A working review designed to separate surface preferences from actual business problems before more money is spent on design or development.",
    whyItMatters:
      "Most weak brand and website outcomes are the result of starting the wrong project. An audit clarifies the real bottleneck and protects the next step from being a guess.",
    whatStrongerLooks:
      "A clear written diagnosis, a short list of priorities, and a recommended sequence that any reasonable owner can review in a single sitting.",
    signs: [
      "You can feel something is off but cannot name the strongest fix.",
      "You have invested in design before and the business outcome did not change.",
      "Different team members would describe the brand or offer differently.",
      "The website ranks fine but inquiries are not landing.",
      "You are about to invest in a larger project and want a second opinion shaped by commercial judgment first.",
    ],
    commonMistakes: [
      "Skipping an audit and committing to a full rebuild based on instinct.",
      "Confusing an audit with a sales pitch for the next big package.",
      "Treating the audit as a checklist instead of a decision tool.",
    ],
    howToUse:
      "Read the audit twice. Use the priority list to choose the single project that unblocks the most opportunity. Bring the audit to any future vendor conversation so scope debates start from facts.",
    whenNot:
      "Skip the audit if you already know the bottleneck and want to move directly into a brand, website, or local visibility project. Book a strategy call instead.",
  },
  "brand-systems": {
    whatThisIs:
      "A working identity system that helps the business look consistent everywhere a customer judges it.",
    whyItMatters:
      "A weak brand quietly makes a real business look smaller, newer, or less organized than it is. Customers rarely say this out loud, but they make faster trust decisions when the website, social profiles, signage, cards, and sales materials feel like the same business.",
    whatStrongerLooks:
      "Logo, colour, typography, and voice that hold together across web, print, social, and sales. Buyers stop questioning the seriousness of the company.",
    signs: [
      "You explain your business differently every time.",
      "Touchpoints look like five separate companies.",
      "Founders or salespeople apologize for the brand in meetings.",
      "Your business looks less established online than it is in real life.",
      "You hesitate to send people to your website or social channels.",
    ],
    commonMistakes: [
      "Treating a logo refresh as a full brand system.",
      "Designing the website before the identity is settled.",
      "Buying a generic template kit instead of a brand that matches the work.",
      "Posting more content before the visual system is consistent.",
    ],
    howToUse:
      "Roll the system out in the order customers actually see it: website, social profiles, sales materials, then print. Hold every new asset to the brand guide.",
    whenNot:
      "Skip the foundation if your real bottleneck is a missing offer, a broken website conversion path, or an empty Google Business Profile. Fix that first.",
  },
  "website-systems": {
    whatThisIs:
      "A website built to explain the offer, support trust, and move the right visitors toward the next step without making them hunt.",
    whyItMatters:
      "A website should not require the visitor to decode the business. Page structure, copy hierarchy, and calls to action exist to reduce uncertainty, not to decorate.",
    whatStrongerLooks:
      "Clear navigation, real service pages, honest proof, an obvious next step, and mobile experience that is just as crisp as desktop.",
    signs: [
      "Traffic is decent but inquiries are thin.",
      "Visitors ask questions the website should already answer.",
      "The homepage is the only page that has been thought through.",
      "Sales calls keep repeating the same explanations.",
      "Mobile experience falls apart below the hero.",
    ],
    commonMistakes: [
      "Redesigning the homepage before fixing the offer.",
      "Adding more pages before the core service path is clear.",
      "Confusing visual polish with conversion structure.",
      "Hiding pricing where pricing could be shown.",
    ],
    howToUse:
      "Treat the live site as a product. Review it quarterly. Update offers, proof, and service pages as the business actually changes.",
    whenNot:
      "Skip the website project if the identity, offer, or positioning is still in flux. Stabilize the brand and the offer first.",
  },
  "brand-and-website-packages": {
    whatThisIs:
      "A combined brand and website system delivered together so the identity and the public presence ship as one decision.",
    whyItMatters:
      "Doing brand and website separately often produces two halves that never quite meet. The combined path makes the studio responsible for the way both feel side by side.",
    whatStrongerLooks:
      "A consistent identity, a structured website, and the sales path connecting them, all signed off in one engagement.",
    signs: [
      "Both the brand and the website need work and you do not want to coordinate two vendors.",
      "The current public presence is held together by patches.",
      "You are entering a more serious chapter of the business and need both halves to match.",
    ],
    commonMistakes: [
      "Trying to coordinate a designer, a developer, and a marketer who are not working from the same scope.",
      "Letting the website team make brand decisions or vice versa.",
    ],
    howToUse:
      "Use the brand guide and the website together as the new baseline. Hold any future supplier to it.",
    whenNot:
      "Skip the combined package if only one half is the problem. A focused brand or website project is cheaper and faster.",
  },
  "local-visibility": {
    whatThisIs:
      "The set of moves that helps nearby buyers find, trust, and choose the business when they are already comparing options.",
    whyItMatters:
      "Local visibility is not only about being found. It is about appearing complete, current, credible, and easy to contact in the moment someone is deciding.",
    whatStrongerLooks:
      "A fully built Google Business Profile, a website that supports it, real reviews handled in real language, and customer-facing assets that close the loop in person.",
    signs: [
      "The profile is incomplete or empty.",
      "Reviews come in by accident, not by request.",
      "Categories or services on the profile do not match what the business actually does.",
      "Photos are old, missing, or off-brand.",
      "Walk-ins or call-ins say they almost did not find you.",
    ],
    commonMistakes: [
      "Trusting any vendor who guarantees rankings.",
      "Buying or incentivising fake reviews.",
      "Setting up the profile once and never touching it again.",
      "Treating local visibility as separate from the website.",
    ],
    howToUse:
      "Treat the profile as a live business asset. Update offers, photos, and services as the business actually changes. Ask the right customers for honest reviews and respond to every one.",
    whenNot:
      "Skip local visibility work if the business is fully remote or B2B with no physical service area. Invest in brand, website, and sales materials instead.",
  },
  "content-and-social": {
    whatThisIs:
      "Practical creative and planning support so the brand looks active, consistent, and credible across the channels customers actually check.",
    whyItMatters:
      "Social media does not need to explain everything. It needs to make the business feel active, professional, and easy to understand at a glance.",
    whatStrongerLooks:
      "Profiles that match the website, a cadence the team can actually hold, and content that supports sales conversations instead of replacing them.",
    signs: [
      "Profiles look like a different business than the website.",
      "Posting is inconsistent or paused for months.",
      "Content is busy but the offer is still unclear.",
      "No one on the team owns the channel.",
    ],
    commonMistakes: [
      "Posting daily before the brand system is consistent.",
      "Chasing every new platform instead of holding the one that already works.",
      "Treating social as the marketing strategy instead of one channel.",
    ],
    howToUse:
      "Use the kit and content rhythm as a base. Improve from there as the business learns what its audience responds to.",
    whenNot:
      "Skip social work if the website cannot yet receive and convert the attention that social would send. Fix the website first.",
  },
  "print-and-sales-materials": {
    whatThisIs:
      "Printed and digital materials that support real sales conversations, follow-ups, and in-person touchpoints.",
    whyItMatters:
      "A printed piece often gets judged in seconds. Good design helps the reader understand what it is, why it matters, and what to do next, without a sales pitch.",
    whatStrongerLooks:
      "Materials that match the brand, explain the offer, and leave the customer with confidence after a meeting or visit.",
    signs: [
      "Sales calls go well but follow-up materials are weak.",
      "Cards, sheets, or flyers look older than the business.",
      "Reps print their own versions of the same document.",
      "Decks feel cluttered and lose serious buyers.",
    ],
    commonMistakes: [
      "Designing print before the brand system is settled.",
      "Cramming the page with everything the business does.",
      "Using stock imagery that does not match the work.",
    ],
    howToUse:
      "Use sales materials in follow-up emails, printed leave-behinds, direct outreach, trade shows, consultations, and partner conversations.",
    whenNot:
      "Skip print work if the website cannot back up the claims in the printed piece. Strengthen the digital story first.",
  },
  "digital-product-support": {
    whatThisIs:
      "Practical support for turning a workbook, checklist, template, or guide into a digital product that looks credible and easy to buy.",
    whyItMatters:
      "A useful resource can still fail if the buyer cannot quickly understand what it is, who it is for, and how they will use it.",
    whatStrongerLooks:
      "A clear product cover, a sharp listing description, a believable sales visual, and a price that matches the perceived value.",
    signs: [
      "The product is good but the cover does not show it.",
      "Listing copy reads like a draft instead of a sales asset.",
      "Buyers ask basic questions before purchasing.",
      "The product looks unfinished next to competitor listings.",
    ],
    commonMistakes: [
      "Launching with a screenshot as the product cover.",
      "Writing listing copy as a list of contents only.",
      "Hiding the product behind a sign-up wall when it could just be sold.",
    ],
    howToUse:
      "Use the polished cover, copy, and mockup wherever the product is sold or shared. Treat the listing as a sales page, not a filing card.",
    whenNot:
      "Skip product support if the underlying resource is not finished. Finish the content before paying to present it.",
  },
  "platform-brand-kits": {
    whatThisIs:
      "A small, focused brand direction kit prepared for one of the platforms the business already uses every day.",
    whyItMatters:
      "Most teams will not learn a new design tool. They will keep using Canva, GoDaddy Studio, or Adobe Express. The kit makes those tools produce on-brand work.",
    whatStrongerLooks:
      "Fonts, colours, logo direction, and a handful of working templates ready inside the tool the team already opens daily.",
    signs: [
      "Every new social post looks like a different brand.",
      "The team makes their own versions of the same flyer.",
      "Hours are lost guessing fonts and colours each week.",
    ],
    commonMistakes: [
      "Buying a generic preset pack and calling it a brand system.",
      "Skipping the brand foundation when the business needs one.",
      "Treating the kit as a replacement for commercial design judgment.",
    ],
    howToUse:
      "Keep the kit pinned inside the platform. Use it as the only starting point for new posts, flyers, and materials.",
    whenNot:
      "Skip the platform kit if the business needs a real brand foundation or website before tactical templates make sense.",
  },
  "marketing-support": {
    whatThisIs:
      "Monthly creative and planning support that keeps marketing moving without forcing the business to hire in-house too early.",
    whyItMatters:
      "Marketing falls apart in the gaps between projects. A monthly cadence keeps the brand active and the campaigns consistent.",
    whatStrongerLooks:
      "A monthly plan, owned priorities, agreed deliverables, and a steady rhythm of small wins.",
    signs: [
      "Marketing dies whenever the founder gets busy.",
      "Small design and content tasks pile up.",
      "Campaigns get launched late or not at all.",
    ],
    commonMistakes: [
      "Hiring a junior in-house marketer before the system is built.",
      "Buying a retainer with no defined monthly priorities.",
    ],
    howToUse:
      "Treat the retainer as an operating system. Plan the month, ship the work, review what mattered, repeat.",
    whenNot:
      "Skip the retainer if the brand and website foundation is broken. Fix the foundation first, then add a monthly rhythm.",
  },
  "business-launch-support": {
    whatThisIs:
      "Focused launch planning for founders preparing to start, restart, or relaunch a business with the right sequence in place.",
    whyItMatters:
      "Most launches fail because the wrong piece is built first. A clear sequence saves time and reduces the cost of relaunching later.",
    whatStrongerLooks:
      "A founder who knows exactly which three or four things have to be true before launch day.",
    signs: [
      "Brand, website, and visibility decisions are all happening at the same time.",
      "You are about to spend on assets you may not need yet.",
      "The launch date keeps slipping because the order of work is unclear.",
    ],
    commonMistakes: [
      "Launching with a website that the brand cannot support.",
      "Skipping local visibility setup on a launch day.",
      "Spending the launch budget on the wrong half first.",
    ],
    howToUse:
      "Use the recommended sequence as the project plan. Confirm each piece before moving to the next.",
    whenNot:
      "Skip launch planning if you are not actually launching anytime soon. A general strategy call is more useful.",
  },
  "custom-projects": {
    whatThisIs:
      "A bespoke scope for engagements that cross brand, website, sales materials, launch, and visibility work.",
    whyItMatters:
      "Real businesses outgrow standard packages. A clear written scope keeps a complex project safe to begin and easy to finish.",
    whatStrongerLooks:
      "A scope letter that names the outcome, deliverables, schedule, and price, in plain English.",
    signs: [
      "No single Kostaja service covers what you need.",
      "Several services need to ship together to make sense.",
      "Other vendors have proposed scopes that felt vague or oversized.",
    ],
    commonMistakes: [
      "Approving a custom project with no written scope.",
      "Letting scope expand mid-project without a new agreement.",
    ],
    howToUse:
      "Treat the scope letter as the contract. Anything beyond it is a separate decision, not a default.",
    whenNot:
      "Skip the custom path if a standard Kostaja package already fits. Pay less and ship faster.",
  },
};

// ---------------------------------------------------------------------------
// SERVICE-LEVEL EDUCATION OVERRIDES
// Optional per-service overrides keyed by service slug. Falls back to category.
// ---------------------------------------------------------------------------
export const SERVICE_EDUCATION = {
  "strategy-call": {
    signs: [
      "You are unsure which Kostaja service to start with.",
      "Two vendors have given you very different recommendations.",
      "You are about to spend several thousand dollars and want a second opinion shaped by commercial judgment.",
      "Your team disagrees on which problem to solve first.",
    ],
    whenNot:
      "Skip the strategy call if you already know exactly which fixed-scope service or project you need. Just request it.",
  },
};

// ---------------------------------------------------------------------------
// COMING-SOON LEARN-MORE PLACEHOLDERS
// Used in "Learn more before you decide" blocks across the site.
// status:"comingSoon" renders as Coming soon, never pretend they are published.
// ---------------------------------------------------------------------------
export const LEARN_MORE = [
  { slug: "why-better-website-doesnt-start-with-design", title: "Why a better website does not start with design", type: "note", status: "published" },
  { slug: "how-customers-judge-a-local-business", title: "How customers judge a local business before they call", type: "note", status: "published" },
  { slug: "logo-vs-brand-system", title: "The difference between a logo and a brand system", type: "note", status: "published" },
  { slug: "service-pages-vs-homepage", title: "Why service pages matter more than most homepages", type: "note", status: "published" },
  { slug: "what-gbp-can-and-cannot-fix", title: "What a Google Business Profile can and cannot fix", type: "note", status: "published" },
  { slug: "useful-sales-materials", title: "How to make sales materials useful instead of decorative", type: "note", status: "published" },
  { slug: "package-a-digital-product", title: "How to package a digital product so buyers understand it", type: "note", status: "published" },
  { slug: "real-estate-trust-before-the-showing", title: "Real estate: trust before the showing", type: "episode", status: "comingSoon" },
];

export const learnMoreForCategory = (categorySlug) => {
  // Curated selections per category. Falls back to a short default list.
  const map = {
    "strategy-and-audits": ["why-better-website-doesnt-start-with-design", "service-pages-vs-homepage", "logo-vs-brand-system"],
    "brand-systems": ["logo-vs-brand-system", "useful-sales-materials", "why-better-website-doesnt-start-with-design"],
    "website-systems": ["service-pages-vs-homepage", "why-better-website-doesnt-start-with-design", "how-customers-judge-a-local-business"],
    "brand-and-website-packages": ["logo-vs-brand-system", "service-pages-vs-homepage", "useful-sales-materials"],
    "local-visibility": ["what-gbp-can-and-cannot-fix", "how-customers-judge-a-local-business"],
    "content-and-social": ["useful-sales-materials", "service-pages-vs-homepage"],
    "print-and-sales-materials": ["useful-sales-materials", "logo-vs-brand-system"],
    "digital-product-support": ["package-a-digital-product", "useful-sales-materials"],
    "platform-brand-kits": ["logo-vs-brand-system", "useful-sales-materials"],
    "marketing-support": ["useful-sales-materials", "service-pages-vs-homepage"],
    "business-launch-support": ["logo-vs-brand-system", "service-pages-vs-homepage"],
    "custom-projects": ["logo-vs-brand-system", "service-pages-vs-homepage", "useful-sales-materials"],
  };
  const slugs = map[categorySlug] || ["why-better-website-doesnt-start-with-design", "logo-vs-brand-system"];
  return slugs.map((s) => LEARN_MORE.find((l) => l.slug === s)).filter(Boolean);
};

export const learnMoreForIndustry = (industrySlug) => {
  const map = {
    "real-estate": ["real-estate-trust-before-the-showing", "service-pages-vs-homepage", "useful-sales-materials"],
  };
  const slugs = map[industrySlug] || ["how-customers-judge-a-local-business", "service-pages-vs-homepage"];
  return slugs.map((s) => LEARN_MORE.find((l) => l.slug === s)).filter(Boolean);
};

// ---------------------------------------------------------------------------
// CTA "WHAT THIS ACTION DOES" COPY by cta.kind
// Used near purchase, quote, subscribe, book buttons.
// ---------------------------------------------------------------------------
export const CTA_CONTEXT = {
  book:
    "Booking opens Calendly. You receive a confirmation. Send a short context note before the call so the session opens on the real question, not the warm-up.",
  shop:
    "The shop runs on Shopify. After purchase you receive instant access by email. Shopify handles checkout, receipts, and downloads.",
  stripe:
    "Purchase reserves the work and starts a short intake. Kostaja confirms the scope and begins per the listed timeline. If a payment link is not yet active, the button routes to the contact form so we can confirm in writing first.",
  quote:
    "Requesting a quote starts a short conversation. Kostaja confirms scope, timeline, deliverables, and payment structure in writing before any work begins. A deposit may reserve a start date and apply toward the approved project total.",
  contact:
    "Sending this inquiry brings the message to studio leadership. You will receive a real reply in plain English with a recommended next step, not a sales script.",
};

// ---------------------------------------------------------------------------
// HOMEPAGE EDUCATIONAL TILES
// ---------------------------------------------------------------------------
export const HOME_HELP_FIX = [
  {
    title: "Unclear first impression",
    body: "Fix: a public presence that looks as capable as the business actually is. Prospects arrive believing you are the right pick.",
  },
  {
    title: "Scattered service presentation",
    body: "Fix: services explained so a buyer understands what you do, who it is for, and what to do next within seconds.",
  },
  {
    title: "Weak trust signals",
    body: "Fix: website, social profiles, Google profile, and sales materials all lined up so nothing on the outside undercuts the confidence.",
  },
  {
    title: "Missing buyer path",
    body: "Fix: a clear next step on every page. Attention converts to booked calls, quote requests, and inbound leads.",
  },
  {
    title: "Inconsistent materials",
    body: "Fix: one recognizable brand across web, print, social, email, and in-person materials. Fewer questions, faster yeses.",
  },
  {
    title: "No clear starting point",
    body: "Fix: a scoped recommendation for the strongest first project. Time and budget go to the move that actually matters.",
  },
];

export const HOME_HOW_TO_START = [
  { title: "Start with an audit", body: "Result: a priority list and a scoped first project. No guesswork.", to: "/services" },
  { title: "Start with brand", body: "Result: a credible, consistent, memorable identity buyers recognize.", to: "/services/brand" },
  { title: "Start with website", body: "Result: a site visitors understand quickly and act on.", to: "/services/websites" },
  { title: "Start with local visibility", body: "Result: nearby customers find, trust, and contact you.", to: "/services/marketing" },
  { title: "Start with sales materials", body: "Result: cleaner follow-up. Prospects say yes with less friction.", to: "/services/print-sales-materials" },
  { title: "Start with a strategy call", body: "Result: a named next step for a problem that crosses brand, website, content, and systems.", to: "/services/strategy-call" },
];

// ---------------------------------------------------------------------------
// SERVICES MARKETPLACE EDUCATIONAL TILES
// ---------------------------------------------------------------------------
export const SERVICES_BILLING_GUIDE = [
  { kind: "Fixed-scope", body: "Best when the deliverable is clear. Defined price, defined timeline, defined receivables." },
  { kind: "Monthly support", body: "Best when consistency is the problem. A monthly rhythm with named priorities each cycle." },
  { kind: "Custom projects", body: "Best when the issue crosses brand, website, content, and systems. A written scope precedes any work." },
  { kind: "Strategy call", body: "Best when the right starting point is unclear. Thirty minutes to name the strongest first move." },
];
